import { useEffect, useRef, useState } from "preact/hooks";
import type { SearchParams } from "./api";
import { Icon } from "./icons";

interface Props {
  initial: SearchParams;
  folders: string[];
  autoFocus: boolean;
  onSearch: (params: SearchParams) => void;
  onClose: () => void;
  /** Choose several of the results (left out before there are any). */
  onSelect?: () => void;
}

/** The query and the filters of search_mail: every word must occur (stemmed, Danish and English), OR between
 * alternatives, "quoted phrases", -word to exclude; combined with semantic similarity. Exact match: the words as
 * written instead (search_mail's exact), every message containing them. */
export function SearchForm({ initial, folders, autoFocus, onSearch, onClose, onSelect }: Props) {
  const [p, setP] = useState<SearchParams>(initial);
  const hasFilters = (s: SearchParams) =>
    Boolean(s.folder || s.from || s.to || s.sent_to || s.after || s.before || s.has_attachment || s.include_deleted || s.exclude_trash || s.flagged || s.exact || s.sort === "newest");
  const [open, setOpen] = useState(hasFilters(initial));
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => setP(initial), [JSON.stringify(initial)]);
  useEffect(() => {
    if (autoFocus) input.current?.focus();
  }, [autoFocus]);

  const set = <K extends keyof SearchParams>(key: K, value: SearchParams[K]) => setP({ ...p, [key]: value || undefined });
  const submit = (e: Event) => {
    e.preventDefault();
    input.current?.blur();
    onSearch(p);
  };

  return (
    <form class="search" onSubmit={submit} role="search">
      <div class="search-row">
        <button type="button" class="icon-button" title="Close search" onClick={onClose}>
          <Icon name="back" />
        </button>
        <input
          ref={input}
          type="search"
          enterKeyHint="search"
          placeholder="Search mail"
          value={p.q ?? ""}
          onInput={(e) => set("q", (e.target as HTMLInputElement).value)}
        />
        <button type="button" class={`icon-button ${open || hasFilters(p) ? "active" : ""}`} title="Filters" onClick={() => setOpen(!open)}>
          <Icon name="filter" />
        </button>
        {onSelect && (
          <button type="button" class="icon-button" title="Select messages" onClick={onSelect}>
            <Icon name="select" />
          </button>
        )}
      </div>
      {open && (
        <div class="filters">
          <label>
            From
            <input type="text" value={p.from ?? ""} placeholder="name or address" onInput={(e) => set("from", (e.target as HTMLInputElement).value)} />
          </label>
          <label>
            To
            <input type="text" value={p.to ?? ""} placeholder="name or address" onInput={(e) => set("to", (e.target as HTMLInputElement).value)} />
          </label>
          <label>
            Sent to
            <input
              type="text"
              value={p.sent_to ?? ""}
              placeholder="an address, or part of one: @domain"
              title="The address the message was sent to, also when To and Cc do not show it (Bcc, spam, forwards). A whole address finds only itself, a part every address containing it."
              onInput={(e) => set("sent_to", (e.target as HTMLInputElement).value)}
            />
          </label>
          <label>
            Folder
            <select value={p.folder ?? ""} onChange={(e) => set("folder", (e.target as HTMLSelectElement).value)}>
              <option value="">All folders</option>
              {folders.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>
          <label>
            Sort
            <select value={p.sort ?? "relevance"} onChange={(e) => set("sort", (e.target as HTMLSelectElement).value as SearchParams["sort"])}>
              <option value="relevance">Best match</option>
              <option value="newest">Newest first</option>
            </select>
          </label>
          <label class="check" title="The words as written: 'faktura' does not find 'fakturaen'. Case still does not matter.">
            <input type="checkbox" checked={!!p.exact} onChange={(e) => set("exact", (e.target as HTMLInputElement).checked)} />
            Exact match
          </label>
          <label>
            After
            <input type="date" value={p.after ?? ""} onInput={(e) => set("after", (e.target as HTMLInputElement).value)} />
          </label>
          <label>
            Before
            <input type="date" value={p.before ?? ""} onInput={(e) => set("before", (e.target as HTMLInputElement).value)} />
          </label>
          <label class="check">
            <input type="checkbox" checked={!!p.has_attachment} onChange={(e) => set("has_attachment", (e.target as HTMLInputElement).checked)} />
            With attachments
          </label>
          <label class="check">
            <input type="checkbox" checked={!!p.flagged} onChange={(e) => set("flagged", (e.target as HTMLInputElement).checked)} />
            Only starred
          </label>
          <label class="check">
            <input type="checkbox" checked={!!p.include_deleted} onChange={(e) => set("include_deleted", (e.target as HTMLInputElement).checked)} />
            Include deleted mail
          </label>
          <label class="check" title="Leaves out the mail in Trash, unless it is also in another folder.">
            <input type="checkbox" checked={!!p.exclude_trash} onChange={(e) => set("exclude_trash", (e.target as HTMLInputElement).checked)} />
            Exclude Trash
          </label>
          <div class="filter-actions">
            <button type="button" class="text-button" onClick={() => setP({ q: p.q })}>
              Clear filters
            </button>
            <button type="submit" class="primary">
              Search
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
